/*
 * 文件职责 / File Purpose
 * - 中文：合并自选/策略/报告概览与指定标的的分型预计算状态，形成研究页面共享摘要。
 * - English: Combines watchlist/strategy/report overview data with one symbol's fractal-precompute status into a shared research digest.
 *
 * 数据与时间边界 / Data and Temporal Boundary
 * - 中文：Hook 只调用既有前端 service，并在 symbol 或 resolution 变化时丢弃过期回写；它不触发预计算、不计算分型结构，也不补造缺失状态。
 * - English: The hook calls existing frontend services only and discards stale writes when symbol or resolution changes; it triggers no precompute, computes no fractal structure, and invents no missing status.
 */
import { useEffect, useMemo, useState } from 'react';
import fractalApi from '@/services/fractalApi';
import { getResearchWatchlistWorkbench } from '@/services/researchWatchlists';
import { getStrategyReportCatalog } from '@/services/strategyReportArchive';
import { unwrapResponse } from '@/features/research-workspace/watchlists/model';
import { REPORT_CATEGORY_LABELS } from '@/pages/reports/reportRegistry';

const DEFAULT_RESOLUTION = 'D';

const toArray = value => (Array.isArray(value) ? value : []);
const toSafeNumber = value => (Number.isFinite(Number(value)) ? Number(value) : 0);

const useResearchDigest = ({ symbol = null, resolution = DEFAULT_RESOLUTION } = {}) => {
  const [overviewState, setOverviewState] = useState({
    groups: [],
    loading: true,
    partialError: false,
    reportItems: [],
    stockCount: 0,
    strategyItems: [],
  });
  const {
    groups,
    loading: overviewLoading,
    partialError,
    reportItems,
    stockCount,
    strategyItems,
  } = overviewState;
  const safeGroups = toArray(groups);
  const safeStrategyItems = toArray(strategyItems);
  const safeReportItems = toArray(reportItems);
  const safeStockCount = toSafeNumber(stockCount);
  const safeReportCategoryCount = useMemo(() => new Set(
    safeReportItems.map(item => REPORT_CATEGORY_LABELS[item.category] || '策略报告'),
  ).size, [safeReportItems]);

  useEffect(() => {
    let mounted = true;
    Promise.allSettled([
      getResearchWatchlistWorkbench({
        mode: 'table',
        page: 1,
        pageSize: 40,
        sortBy: 'attentionRequired',
        sortOrder: 'desc',
        tab: 'tracking',
      }),
      getStrategyReportCatalog(),
    ]).then(([trackingResult, catalogResult]) => {
      if (!mounted) return;
      let tracking = null;
      let catalog = [];
      let hasError = false;
      try {
        tracking = trackingResult.status === 'fulfilled'
          ? unwrapResponse(trackingResult.value)
          : null;
      } catch (error) {
        tracking = null;
      }
      if (!tracking) hasError = true;

      if (catalogResult.status === 'fulfilled' && catalogResult.value?.success) {
        catalog = toArray(catalogResult.value.data);
        if (!Array.isArray(catalogResult.value.data)) hasError = true;
      } else {
        hasError = true;
      }

      const trackingItems = toArray(tracking?.items);
      const nextStockCount = toSafeNumber(
        tracking?.page?.total ?? tracking?.counts?.tracking,
      );
      setOverviewState({
        groups: nextStockCount > 0
          ? [{
            groupId: 'continued_tracking',
            name: '持续跟踪',
            stocks: trackingItems,
          }]
          : [],
        loading: false,
        partialError: hasError,
        reportItems: catalog.filter(item => (
          item && (!item.importable || item.mdFilename)
        )),
        stockCount: nextStockCount,
        strategyItems: catalog.filter(item => item?.importable),
      });
    });
    return () => {
      mounted = false;
    };
  }, []);

  const [statusSummary, setStatusSummary] = useState(null);
  const [latestStatus, setLatestStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(Boolean(symbol));
  const [statusError, setStatusError] = useState(null);

  useEffect(() => {
    let mounted = true;

    if (!symbol) {
      setStatusSummary(null);
      setLatestStatus(null);
      setStatusLoading(false);
      setStatusError(null);
      return () => {
        mounted = false;
      };
    }

    const loadStatus = async () => {
      setStatusLoading(true);
      setStatusError(null);

      const response = await fractalApi.getStatus(symbol, resolution);

      if (!mounted) {
        return;
      }

      if (response && response.success) {
        const summary = response.data?.summary || null;
        const statusList = Array.isArray(response.data?.statusList) ? response.data.statusList : [];
        setStatusSummary(summary);
        setLatestStatus(statusList[0] || null);
        setStatusError(null);
      } else {
        setStatusSummary(null);
        setLatestStatus(null);
        setStatusError(response?.error || response?.errmsg || 'status_unavailable');
      }

      setStatusLoading(false);
    };

    loadStatus();

    return () => {
      mounted = false;
    };
  }, [resolution, symbol]);

  const overview = useMemo(() => ({
    groupCount: safeGroups.length,
    stockCount: safeStockCount,
    strategyCount: safeStrategyItems.length,
    reportCount: safeReportItems.length,
    reportCategoryCount: safeReportCategoryCount,
  }), [
    safeGroups.length,
    safeStockCount,
    safeStrategyItems.length,
    safeReportItems.length,
    safeReportCategoryCount,
  ]);

  return {
    groups: safeGroups,
    strategyItems: safeStrategyItems,
    reportItems: safeReportItems,
    overviewLoading,
    partialError,
    stockCount: safeStockCount,
    reportCategoryCount: safeReportCategoryCount,
    statusSummary,
    latestStatus,
    statusLoading,
    statusError,
    overview,
  };
};

export default useResearchDigest;
